import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Heart, Calendar, ShieldCheck, AlertCircle, ShoppingBag, HandHeart, Sparkles, ArrowRight } from "lucide-react";
import { API } from "../lib/api";
import SEO from "../components/SEO";
import TierBadge from "../components/TierBadge";

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return iso; } };

const Portal = () => {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!token) { setError("No portal link provided."); setLoading(false); return; }
        (async () => {
            try {
                const res = await fetch(`${API}/portal/me?token=${encodeURIComponent(token)}`);
                const body = await res.json();
                if (!res.ok) throw new Error(body.detail || "Unable to load portal");
                setData(body);
            } catch (e) {
                setError(e.message || "Unable to load portal");
            } finally {
                setLoading(false);
            }
        })();
    }, [token]);

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center" data-testid="portal-loading">
                <p className="text-[#4A5568]">Opening your portal…</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-md mx-auto px-6 py-20 text-center" data-testid="portal-error">
                <SEO title="Donor portal · Reddog Foundation" />
                <AlertCircle className="w-12 h-12 text-[#E53935] mx-auto mb-4" strokeWidth={1.5} />
                <h1 className="font-heading text-2xl text-[#1C252E] mb-2">Portal link unavailable</h1>
                <p className="text-sm text-[#4A5568] mb-6">{error}</p>
                <p className="text-sm text-[#4A5568] mb-2">Need a fresh link?</p>
                <Link to="/account" data-testid="portal-request-link" className="inline-block bg-[#E53935] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:opacity-90 transition">
                    Sign in or request a new link
                </Link>
            </div>
        );
    }

    const { email, name, lifetime_total, lifetime_count, by_year, recent, active_subscriptions, contributed_projects, token_ttl_days, tier } = data;

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-testid="portal-page">
            <SEO title="Your donor portal · Reddog Foundation" />

            <div className="mb-8">
                <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-2">Donor portal</p>
                <h1 className="font-heading text-3xl sm:text-4xl text-[#1C252E] font-medium tracking-tight" data-testid="portal-heading">
                    Welcome back{name ? `, ${name}` : ""}.
                </h1>
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                    <p className="text-sm text-[#4A5568]" data-testid="portal-email">{email}</p>
                    {tier && <TierBadge tier={tier} size="lg" data-testid="portal-tier-badge" />}
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <div className="bg-[#1C252E] text-white rounded-2xl p-5" data-testid="portal-lifetime-total">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#F2D8D5] font-bold mb-1">Lifetime giving</p>
                    <p className="text-3xl font-medium">{fmtMoney(lifetime_total)}</p>
                    <p className="text-xs text-[#F2D8D5] mt-1">{lifetime_count} {lifetime_count === 1 ? "gift" : "gifts"}</p>
                </div>
                <div className="bg-white border border-black/10 rounded-2xl p-5" data-testid="portal-subscriptions">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#4A5568] font-bold mb-1">Active recurring</p>
                    <p className="text-3xl font-medium text-[#1C252E]">{active_subscriptions}</p>
                    <p className="text-xs text-[#4A5568] mt-1">subscription{active_subscriptions === 1 ? "" : "s"}</p>
                </div>
                <div className="bg-white border border-black/10 rounded-2xl p-5" data-testid="portal-projects">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#4A5568] font-bold mb-1">Projects funded</p>
                    <p className="text-3xl font-medium text-[#1C252E]">{contributed_projects}</p>
                    <p className="text-xs text-[#4A5568] mt-1">since your first gift</p>
                </div>
            </div>

            {/* Empty-state "Get started" trio — only when the donor has zero history. */}
            {lifetime_count === 0 && (
                <div className="bg-white border border-[#E53935]/20 rounded-2xl p-6 sm:p-8 mb-8" data-testid="portal-empty-state">
                    <div className="flex items-start gap-3 mb-5">
                        <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#F2D8D5] text-[#E53935] shrink-0">
                            <Sparkles className="w-5 h-5" strokeWidth={1.5} />
                        </span>
                        <div>
                            <h2 className="font-heading text-2xl text-[#1C252E] tracking-tight leading-tight">A great place to start</h2>
                            <p className="text-sm text-[#4A5568] mt-1">Three ways to plug in right now. Pick one and your portal will start filling up with the impact you make.</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <Link to="/donate" data-testid="portal-cta-donate" className="group bg-[#FDFBF7] border border-black/5 rounded-2xl p-5 hover:border-[#E53935]/40 hover:shadow-md transition flex flex-col">
                            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#E53935] text-white mb-3"><Heart className="w-4 h-4" strokeWidth={2} /></span>
                            <p className="font-heading text-lg text-[#1C252E] leading-snug">Make your first gift</p>
                            <p className="text-xs text-[#4A5568] mt-1 flex-1">One-time or monthly — every dollar lands on a public ledger.</p>
                            <p className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#E53935] group-hover:gap-2 transition-all">Donate <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} /></p>
                        </Link>
                        <Link to="/shop" data-testid="portal-cta-shop" className="group bg-[#FDFBF7] border border-black/5 rounded-2xl p-5 hover:border-[#E53935]/40 hover:shadow-md transition flex flex-col">
                            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#1C252E] text-white mb-3"><ShoppingBag className="w-4 h-4" strokeWidth={2} /></span>
                            <p className="font-heading text-lg text-[#1C252E] leading-snug">Buy from the shop</p>
                            <p className="text-xs text-[#4A5568] mt-1 flex-1">Foundation merchandise. Proceeds fund programs directly.</p>
                            <p className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#E53935] group-hover:gap-2 transition-all">Shop <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} /></p>
                        </Link>
                        <Link to="/get-involved" data-testid="portal-cta-volunteer" className="group bg-[#FDFBF7] border border-black/5 rounded-2xl p-5 hover:border-[#E53935]/40 hover:shadow-md transition flex flex-col">
                            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-amber-500 text-white mb-3"><HandHeart className="w-4 h-4" strokeWidth={2} /></span>
                            <p className="font-heading text-lg text-[#1C252E] leading-snug">Volunteer your time</p>
                            <p className="text-xs text-[#4A5568] mt-1 flex-1">Events, drives, board roles — find a fit for your schedule.</p>
                            <p className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#E53935] group-hover:gap-2 transition-all">Get involved <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} /></p>
                        </Link>
                    </div>
                </div>
            )}

            {by_year && by_year.length > 0 && (
                <div className="bg-white border border-black/10 rounded-2xl p-6 mb-8" data-testid="portal-by-year">
                    <h2 className="text-base font-medium text-[#1C252E] mb-4 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-[#E53935]" strokeWidth={1.5} />
                        Yearly summary
                    </h2>
                    <div className="space-y-2">
                        {by_year.map((y) => (
                            <div key={y.year} className="flex items-center justify-between py-2 border-b border-black/5 last:border-0" data-testid={`portal-year-${y.year}`}>
                                <span className="font-medium text-[#1C252E]">{y.year}</span>
                                <span className="text-sm text-[#4A5568]">{y.count} {y.count === 1 ? "gift" : "gifts"}</span>
                                <span className="font-medium text-[#1C252E]">{fmtMoney(y.total)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {recent && recent.length > 0 && (
                <div className="bg-white border border-black/10 rounded-2xl p-6 mb-8" data-testid="portal-recent">
                    <h2 className="text-base font-medium text-[#1C252E] mb-4 flex items-center gap-2">
                        <Heart className="w-4 h-4 text-[#E53935]" strokeWidth={1.5} />
                        Recent gifts
                    </h2>
                    <div className="space-y-3">
                        {recent.map((g) => (
                            <div key={g.id} className="flex items-start justify-between py-2 border-b border-black/5 last:border-0" data-testid={`portal-gift-${g.id}`}>
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-[#1C252E]">{g.campaign_name || "General support"}</p>
                                    {g.tribute_name && (
                                        <p className="text-xs text-[#4A5568] mt-0.5">
                                            In {g.tribute_type === "memory" ? "memory" : "honor"} of {g.tribute_name}
                                        </p>
                                    )}
                                    <p className="text-xs text-[#4A5568] mt-0.5">{fmtDate(g.created_at)}</p>
                                </div>
                                <span className="font-medium text-[#1C252E] ml-4">{fmtMoney(g.amount)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="bg-[#F4F1EA] rounded-2xl p-6 flex items-start gap-3" data-testid="portal-security-note">
                <ShieldCheck className="w-5 h-5 text-[#4A5568] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                    <p className="text-sm text-[#1C252E] font-medium">Read-only portal · expires in {token_ttl_days} days</p>
                    <p className="text-xs text-[#4A5568] mt-1">
                        This link was sent to your email and grants temporary, read-only access. To make changes or download tax letters,{" "}
                        <Link to="/account" className="text-[#E53935] underline">sign in to your account</Link>.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Portal;
